import { xorWith } from "lodash";
import * as React from "react";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { PostCodeAnywhereSearchEditor } from "../../../../../react-core/Html/Address/PostCodeAnywhereSearchEditor";
import { FormComponent } from "../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../react-core/Html/PartLoading";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import { UkPostCode } from "../../../../../react-core/Html/Validation/RegExPatterns";
import { CustomerType } from "../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { PhysicalAddressType } from "../../../../../Wrap/Constants/Enums/Customer/PhysicalAddressType";
import { CountryCode } from "../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { AddressDetailModel } from "../../../../../Wrap/Models/Models/Customer/AddressDetailModel";
import { AddressHelper } from "../../../../Shared/Common/AddressHelper";
import { CountryCodeMap } from "../../../../Shared/Entities/Maps/CountryCodeMap";
import { DeleteLightbox } from "../../../Common/DeleteLightbox";
import { FullClientDetailHelper } from "../Common/FullClientDetail/FullClientDetailHelper";
import { ClientRecordAction } from "./Action/ClientRecordAction";
import { SectionTable } from "./Components/SectionTable";
import { AddressTypeMap, IClientTabProps } from "./Resource/Contents";

interface IAddressTabState {
    Data: Array<AddressDetailModel>;
    isLoading: boolean;
    showDeleteLightbox: boolean;
    addressToDelete: AddressDetailModel;
}
interface IAddressTabProps extends IClientTabProps {
    customerType: CustomerType;
}

export class Addresses extends FormComponent<IAddressTabProps, IAddressTabState> {
    constructor(props: IAddressTabProps) {
        super(props);
        this.state = {
            Data: [],
            isLoading: true,
            showDeleteLightbox: false,
            addressToDelete: null,
        };
    }

    componentDidMount() {
        ClientRecordAction.getCustomerAddress(Number.parseInt(this.props.id), (val) => {
            this.setState({
                Data: val,
                isLoading: false,
            });
        });
    }

    public render(): JSX.Element {
        if (this.state.isLoading) {
            return <PartLoading isLoading={true} />;
        }
        const primaryResidential = this.state.Data.filter(
            (x) =>
                x.PhysicalAddressType == PhysicalAddressType.Residential ||
                x.PhysicalAddressType == PhysicalAddressType.Registered
        );
        const postalAddress = this.state.Data.filter(
            (x) =>
                x.PhysicalAddressType == PhysicalAddressType.Postal &&
                !primaryResidential.find((ele) => FullClientDetailHelper.isSameAddress(ele.Address, x.Address))
        );
        return (
            <>
                <div className="panel-subtle pl-34 pt-38">
                    {this.renderSection(
                        primaryResidential,
                        !primaryResidential[0]?.PhysicalAddressType
                            ? this.props.customerType == CustomerType.CorporateTrust ||
                              this.props.customerType == CustomerType.Company
                                ? PhysicalAddressType.Registered
                                : PhysicalAddressType.Residential
                            : primaryResidential[0]?.PhysicalAddressType
                    )}
                    <hr className="mb-32 mt-20" />
                    {this.renderSection(postalAddress, PhysicalAddressType.Postal)}
                </div>
                {this.state.showDeleteLightbox && (
                    <DeleteLightbox
                        onClose={() => this.setState({ showDeleteLightbox: false })}
                        onDelete={() => this.onRemove(this.state.addressToDelete)}
                        content={<p>Are you sure you want to delete this address?</p>}
                    />
                )}
            </>
        );
    }

    private renderSection(address: Array<AddressDetailModel>, type: PhysicalAddressType): JSX.Element {
        if (
            type == PhysicalAddressType.Postal &&
            !this.state.Data.some(
                (x) =>
                    x.PhysicalAddressType == PhysicalAddressType.Residential ||
                    x.PhysicalAddressType == PhysicalAddressType.Registered
            )
        ) {
            return (
                <>
                    <SectionTable
                        sectionHeader={AddressTypeMap[type]}
                        columnConfigs={[]}
                        lightboxContent={(data, onDataChange) =>
                            this.renderLightBox(data, onDataChange, AddressTypeMap[type])
                        }
                        key={AddressTypeMap[type]}
                        data={{ PhysicalAddressType: type }}
                        canEdit={this.props?.hasReadWriteAccess == true}
                    />
                </>
            );
        } else if (address.length == 0) {
            if (this.props?.hasReadWriteAccess != true) {
                return (
                    <>
                        <SectionTable
                            sectionHeader={AddressTypeMap[type]}
                            columnConfigs={[]}
                            lightboxContent={(data, onDataChange) =>
                                this.renderLightBox(data, onDataChange, AddressTypeMap[type])
                            }
                            key={AddressTypeMap[type]}
                            data={{ PhysicalAddressType: type }}
                            canEdit={false}
                        />
                    </>
                );
            } else {
                return (
                    <>
                        <SectionTable
                            sectionHeader={AddressTypeMap[type]}
                            columnConfigs={[]}
                            lightboxContent={(data, onDataChange) =>
                                this.renderLightBox(data, onDataChange, AddressTypeMap[type])
                            }
                            key={AddressTypeMap[type]}
                            onAdd={(data) => this.onAdd(data)}
                            data={{ PhysicalAddressType: type }}
                        />
                    </>
                );
            }
        } else {
            let columnConfigs = [
                { title: "Country", data: CountryCodeMap.getString(address[0].Address.Country) },
                { title: "Line1", data: address[0].Address.Address1 },
                { title: "Line2", data: address[0].Address.Address2 },
                { title: "Line3", data: address[0].Address.Address3 },
            ];
            if (!CommonHelper.isNullOrEmpty(address[0]?.Address?.Address4)) {
                columnConfigs = columnConfigs.concat({ title: "Line4", data: address[0].Address.Address4 });
            }
            columnConfigs = columnConfigs.concat({ title: "Postcode", data: address[0].Address.PostCode });
            return (
                <>
                    <SectionTable
                        sectionHeader={AddressTypeMap[type]}
                        columnConfigs={columnConfigs}
                        data={address[0]}
                        lightboxContent={(data, onDataChange) =>
                            this.renderLightBox(data, onDataChange, AddressTypeMap[type])
                        }
                        key={AddressTypeMap[type]}
                        onSave={(data) => (this.props?.hasReadWriteAccess != true ? null : this.onSave(data))}
                        onRemove={
                            address[0].PhysicalAddressType == PhysicalAddressType.Residential ||
                            address[0].PhysicalAddressType == PhysicalAddressType.Registered ||
                            this.props?.hasReadWriteAccess != true
                                ? null
                                : () => this.setState({ addressToDelete: address[0], showDeleteLightbox: true })
                        }
                        canEdit={this.props?.hasReadWriteAccess == true}
                    />
                </>
            );
        }
    }
    private onSave(data): boolean {
        if (this.isValid()) {
            const type = data.PhysicalAddressType;
            const { Data } = this.state;
            const index = Data.findIndex((ele) => ele.PhysicalAddressType == type);
            Data[index] = data;
            ClientRecordAction.saveCustomerAddress(Number.parseInt(this.props.id), Data, (val) => {
                this.setState({
                    Data: Data,
                    isLoading: false,
                });
            });
            return true;
        }
        return false;
    }

    private onAdd(data): boolean {
        if (this.isValid()) {
            const Data = this.state.Data;
            Data.push(data);
            ClientRecordAction.addCustomerAddress(Number.parseInt(this.props.id), data, () => {
                this.setState({
                    Data: Data,
                    isLoading: false,
                });
            });
            return true;
        }
        return false;
    }

    private onRemove(data) {
        const type = data.PhysicalAddressType;
        const { Data } = this.state;
        const index = Data.findIndex((ele) => ele.PhysicalAddressType == type);
        Data.splice(index, 1);
        ClientRecordAction.saveCustomerAddress(Number.parseInt(this.props.id), Data, (val) => {
            this.setState({
                Data: Data,
                isLoading: false,
            });
        });
    }

    private renderLightBox(ele: AddressDetailModel, onDataChange, header: string): JSX.Element {
        if (!ele?.Address?.Country || ele?.Address?.Country == null) {
            this.onPostCodeSearchChange(
                {
                    Country: CountryCode.GBR,
                },
                onDataChange,
                ele.Address
            );
        }
        const { Data } = this.state;
        const address = Data.find((x) => x.PhysicalAddressType == ele?.PhysicalAddressType);
        return (
            <>
                <SelectEditor
                    options={CountryCodeMap.getArray()}
                    getText={(v) => {
                        return CountryCodeMap.getString(v);
                    }}
                    placeholder="Please select"
                    label={"Country"}
                    required="Please select Country"
                    selected={ele?.Address?.Country}
                    cssWrapper="control-size-430"
                    onChange={(v) => {
                        this.onPostCodeSearchChange(
                            {
                                Country: v,
                            },
                            onDataChange,
                            ele.Address
                        );
                    }}
                />
                <p>AddressType: {header}</p>
                <PostCodeAnywhereSearchEditor
                    onLookupError={() => {}}
                    useLabelForAddress
                    onAddressSelected={(v) => {
                        this.onPostCodeSearchChange(
                            {
                                Address1: v.Line1,
                                Address2: v.Line2,
                                Address3: v.Line3,
                                PostCode: v.Postcode,
                                Country: v.CountryCode,
                            },
                            onDataChange,
                            ele.Address
                        );
                    }}
                    countryCode={ele?.Address?.Country}
                    cssWrapper="control-size-430"
                    label={`Residential address`}
                />
                <TextBoxEditor
                    value={ele?.Address?.Address1}
                    label={"Line1"}
                    required="Please enter line1"
                    cssWrapper="control-size-430"
                    maxLength={100}
                    onChange={(v) => {
                        this.onPostCodeSearchChange(
                            {
                                Address1: v,
                            },
                            onDataChange,
                            ele.Address
                        );
                    }}
                />
                <TextBoxEditor
                    value={ele?.Address?.Address2}
                    label={"Line2"}
                    cssWrapper="control-size-430"
                    maxLength={100}
                    onChange={(v) => {
                        this.onPostCodeSearchChange(
                            {
                                Address2: v,
                            },
                            onDataChange,
                            ele.Address
                        );
                    }}
                />
                <TextBoxEditor
                    value={ele?.Address?.Address3}
                    label={"Line3"}
                    cssWrapper="control-size-430"
                    maxLength={35}
                    onChange={(v) => {
                        this.onPostCodeSearchChange(
                            {
                                Address3: v,
                            },
                            onDataChange,
                            ele.Address
                        );
                    }}
                />
                {!CommonHelper.isNullOrEmpty(address?.Address?.Address4) && (
                    <TextBoxEditor
                        value={ele?.Address?.Address4}
                        label={"Line4"}
                        cssWrapper="control-size-430"
                        maxLength={35}
                        onChange={(v) => {
                            this.onPostCodeSearchChange(
                                {
                                    Address4: v,
                                },
                                onDataChange,
                                ele.Address
                            );
                        }}
                    />
                )}

                <TextBoxEditor
                    value={ele?.Address?.PostCode}
                    label={"Postcode"}
                    cssWrapper="control-size-430"
                    required={ele?.Address?.Country == CountryCode.GBR ? "Please enter postCode" : null}
                    maxLength={8}
                    onChange={(v) => {
                        this.onPostCodeSearchChange(
                            {
                                PostCode: v,
                            },
                            onDataChange,
                            ele.Address
                        );
                    }}
                    regex={
                        ele?.Address?.Country == CountryCode.GBR
                            ? {
                                  value: UkPostCode,
                                  errorMessage: "You must enter a valid UK postcode.",
                              }
                            : null
                    }
                />
            </>
        );
    }

    private onPostCodeSearchChange(changes, onDataChange, data) {
        const _data = JSON.parse(JSON.stringify(data ? data : {}));
        for (const key in changes) {
            _data[key] = changes[key];
        }
        onDataChange({ Address: _data });
    }
}
